/**
 * ArcOutreachTab — Lightweight reviewer CRM inside Marketing Assets dialog.
 *
 * Tracks blogger/reviewer contacts: name, email, platform, status
 * (not_pitched → pitched → responded → arc_requested → reviewed | passed),
 * date pitched, and notes. Stored per-project in MongoDB.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Pencil, Check, X, Loader2, Mail, Link, StickyNote,
  ChevronDown, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { marketingAssetsApi } from '@/lib/api';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  not_pitched:   { label: 'Not pitched',   color: 'bg-muted text-muted-foreground' },
  pitched:       { label: 'Pitched',       color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  responded:     { label: 'Responded',     color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  arc_requested: { label: 'ARC Requested', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  passed:        { label: 'Passed',        color: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' },
  reviewed:      { label: 'Reviewed',      color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
};

const EMPTY_FORM = {
  name: '', email: '', platform: '', status: 'not_pitched', date_pitched: '', notes: '',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_pitched;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium', cfg.color)}>
      {cfg.label}
    </span>
  );
}

function ContactForm({ initial = EMPTY_FORM, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10.5px] text-muted-foreground">Name *</label>
          <Input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Jane Blogger"
            className="h-7 text-[12px]"
            data-testid="arc-contact-name-input"
          />
        </div>
        <div>
          <label className="text-[10.5px] text-muted-foreground">Email</label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="jane@blog.com"
            className="h-7 text-[12px]"
            data-testid="arc-contact-email-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10.5px] text-muted-foreground">Platform / Blog</label>
          <Input
            value={form.platform}
            onChange={(e) => set('platform', e.target.value)}
            placeholder="TheIndieBookshelf.com"
            className="h-7 text-[12px]"
            data-testid="arc-contact-platform-input"
          />
        </div>
        <div>
          <label className="text-[10.5px] text-muted-foreground">Date Pitched</label>
          <Input
            type="date"
            value={form.date_pitched}
            onChange={(e) => set('date_pitched', e.target.value)}
            className="h-7 text-[12px]"
            data-testid="arc-contact-date-input"
          />
        </div>
      </div>
      <div>
        <label className="text-[10.5px] text-muted-foreground">Status</label>
        <Select value={form.status} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="h-7 text-[12px]" data-testid="arc-contact-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-[12px]">{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[10.5px] text-muted-foreground">Notes</label>
        <Textarea
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Responded saying she likes cozy mysteries; follow up in 2 weeks"
          rows={2}
          className="text-[12px] resize-none"
          data-testid="arc-contact-notes-input"
        />
      </div>
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 text-[11.5px]">
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-[11.5px] gap-1"
          disabled={!form.name.trim() || saving}
          onClick={() => onSave(form)}
          data-testid="arc-contact-save-btn"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function ContactRow({ contact, onEdit, onDelete, deleting }) {
  return (
    <div
      data-testid={`arc-contact-row-${contact.id}`}
      className="flex items-start gap-2.5 rounded-lg border border-border p-2.5 hover:bg-muted/20 transition-colors group"
    >
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[12.5px]">{contact.name}</span>
          <StatusBadge status={contact.status} />
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {contact.email && (
            <span className="flex items-center gap-0.5">
              <Mail className="h-3 w-3" />{contact.email}
            </span>
          )}
          {contact.platform && (
            <span className="flex items-center gap-0.5">
              <Link className="h-3 w-3" />{contact.platform}
            </span>
          )}
          {contact.date_pitched && (
            <span>Pitched: {new Date(contact.date_pitched).toLocaleDateString()}</span>
          )}
        </div>
        {contact.notes && (
          <p className="text-[11px] text-muted-foreground flex items-start gap-0.5 mt-0.5">
            <StickyNote className="h-3 w-3 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{contact.notes}</span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => onEdit(contact)}
          className="rounded p-1 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          data-testid={`arc-edit-${contact.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(contact.id)}
          disabled={deleting === contact.id}
          className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-muted-foreground hover:text-red-500"
          data-testid={`arc-delete-${contact.id}`}
        >
          {deleting === contact.id
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

// ─── Status filter + summary ───────────────────────────────────────────
function StatusSummary({ contacts }) {
  const counts = Object.entries(STATUS_CONFIG).reduce((acc, [k]) => {
    acc[k] = contacts.filter((c) => c.status === k).length;
    return acc;
  }, {});
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px]">
      {Object.entries(STATUS_CONFIG).map(([k, v]) => counts[k] > 0 && (
        <span key={k} className={cn('rounded-full px-2 py-0.5', v.color)}>
          {v.label}: {counts[k]}
        </span>
      ))}
    </div>
  );
}

// ─── Main ARC Tab component ───────────────────────────────────────────
export default function ArcOutreachTab({ pid }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null); // contact being edited
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const data = await marketingAssetsApi.listArcContacts(pid);
      setContacts(data.contacts || []);
    } catch { /* no contacts yet */ }
    finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (form) => {
    setSaving(true);
    try {
      const data = await marketingAssetsApi.addArcContact(pid, form);
      setContacts((c) => [...c, data.contact]);
      setShowForm(false);
      toast.success(`${data.contact.name} added to outreach list.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed.');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      const data = await marketingAssetsApi.updateArcContact(pid, editContact.id, form);
      setContacts((c) => c.map((x) => x.id === editContact.id ? data.contact : x));
      setEditContact(null);
      toast.success('Contact updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (cid) => {
    setDeleting(cid);
    try {
      await marketingAssetsApi.deleteArcContact(pid, cid);
      setContacts((c) => c.filter((x) => x.id !== cid));
      toast.success('Contact removed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally { setDeleting(null); }
  };

  const filtered = filterStatus === 'all'
    ? contacts
    : contacts.filter((c) => c.status === filterStatus);

  const pitched  = contacts.filter((c) => c.status !== 'not_pitched').length;
  const reviewed = contacts.filter((c) => c.status === 'reviewed').length;

  return (
    <div className="space-y-3" data-testid="arc-outreach-tab">
      {/* Header + stats */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
            <Users className="h-3.5 w-3.5 text-primary" />
            ARC Outreach Tracker
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Track every reviewer you pitch — from cold outreach to published review.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 h-7 text-[11.5px] flex-shrink-0"
          onClick={() => { setShowForm(true); setEditContact(null); }}
          data-testid="arc-add-contact-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Add reviewer
        </Button>
      </div>

      {/* Inline stats */}
      {contacts.length > 0 && (
        <div className="flex items-center gap-3 text-[11.5px] bg-muted/30 rounded-md px-3 py-2">
          <span className="text-muted-foreground">{contacts.length} total</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-sky-600">{pitched} pitched</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-emerald-600">{reviewed} reviewed</span>
          {contacts.length > 0 && (
            <span className="ml-auto text-muted-foreground">
              {reviewed}/{contacts.length} conversion ({contacts.length ? Math.round((reviewed / contacts.length) * 100) : 0}%)
            </span>
          )}
        </div>
      )}

      {/* Add form */}
      {showForm && !editContact && (
        <ContactForm
          onSave={handleAdd}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      )}

      {/* Status filter */}
      {contacts.length > 4 && (
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-7 text-[12px] w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-[12px]">All statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-[12px]">{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Contact list */}
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-[13px] py-4 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading contacts…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          <Users className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
          <p className="text-[13px] font-medium text-muted-foreground">
            {contacts.length === 0 ? 'No reviewers added yet.' : 'No contacts match this filter.'}
          </p>
          <p className="text-[11.5px] text-muted-foreground/70 mt-1">
            {contacts.length === 0
              ? 'Use the ARC Pitch Email above to reach out, then track each contact here.'
              : 'Try "All statuses" to see all contacts.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            editContact?.id === c.id
              ? (
                <ContactForm
                  key={c.id}
                  initial={{ name: c.name, email: c.email, platform: c.platform, status: c.status, date_pitched: c.date_pitched || '', notes: c.notes }}
                  onSave={handleUpdate}
                  onCancel={() => setEditContact(null)}
                  saving={saving}
                />
              )
              : (
                <ContactRow
                  key={c.id}
                  contact={c}
                  onEdit={setEditContact}
                  onDelete={handleDelete}
                  deleting={deleting}
                />
              )
          ))}
        </div>
      )}

      {contacts.length > 0 && <StatusSummary contacts={contacts} />}
    </div>
  );
}
